import { Box } from '@mui/material';
import { DataGridPremium, type GridColDef, type GridRowOrderChangeParams } from '@mui/x-data-grid-premium';
import { Typography } from '@peritasai/ui-components';
import { Fragment, useMemo } from 'react';
import { AssetItemActions } from '../shared/AssetItemActions';
import { InstrumentThumbnail } from './InstrumentThumbnail';

const instrumentNameCellSx = {
  minWidth: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'hidden',
};

const primaryLabelSx = {
  wordBreak: 'break-word',
};

const metadataRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 0.5,
  minWidth: 0,
  overflow: 'hidden',
};

const metadataLabelSx = {
  color: 'text.secondary',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const metadataSeparatorSx = {
  color: 'text.secondary',
  flexShrink: 0,
};

export interface InstrumentFormItem {
  localId: string;
  catalogItemId: string;
  displayName: string;
  expectedQuantity: number;
  order: number;
  notes: string;
  group?: string | null;
  set?: string | null;
  instrumentTaxonomyClassId?: number | null;
  instrumentTaxonomyClassName?: string | null;
  instrumentTaxonomyCategoryName?: string | null;
  productNumber?: string | null;
}

interface InstrumentListGridProps {
  rows: InstrumentFormItem[];
  onReorder: (params: GridRowOrderChangeParams) => void;
  onQuantityChange: (localId: string, delta: number) => void;
  onNoteClick: (localId: string) => void;
  onDelete: (localId: string) => void;
}

export const InstrumentListGrid = ({
  rows,
  onReorder,
  onQuantityChange,
  onNoteClick,
  onDelete,
}: InstrumentListGridProps) => {
  const columns = useMemo<GridColDef<InstrumentFormItem>[]>(
    () => [
      {
        field: 'thumbnail',
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <InstrumentThumbnail taxonomyClassId={params.row.instrumentTaxonomyClassId} />
        ),
      },
      {
        field: 'displayName',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const groupLabel = params.row.group?.trim();
          const setLabel = params.row.set?.trim();
          const categoryLabel = params.row.instrumentTaxonomyCategoryName?.trim();
          const refNoLabel = params.row.productNumber?.trim();
          const metadataParts = [
            groupLabel && { key: 'group', testId: 'instrument-group-label', value: groupLabel },
            setLabel && {
              key: 'set',
              testId: 'instrument-set-label',
              value: setLabel,
            },
            categoryLabel && {
              key: 'category',
              testId: 'instrument-category-label',
              value: categoryLabel,
            },
            refNoLabel && { key: 'refNo', testId: 'instrument-refno-label', value: refNoLabel },
          ].filter((part): part is { key: string; testId: string; value: string } => !!part);
          return (
            <Box sx={instrumentNameCellSx}>
              <Typography variant="body2" sx={primaryLabelSx}>
                {params.row.displayName}
              </Typography>
              {metadataParts.length > 0 && (
                <Box sx={metadataRowSx}>
                  {metadataParts.map((part, index) => (
                    <Fragment key={part.key}>
                      {index > 0 && (
                        <Typography
                          data-testid="instrument-metadata-separator"
                          variant="caption"
                          sx={metadataSeparatorSx}
                        >
                          ·
                        </Typography>
                      )}
                      <Typography data-testid={part.testId} variant="caption" sx={metadataLabelSx}>
                        {part.value}
                      </Typography>
                    </Fragment>
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        field: 'actions',
        width: 168,
        sortable: false,
        renderCell: (params) => (
          <AssetItemActions
            id={params.row.localId}
            quantity={params.row.expectedQuantity}
            note={params.row.notes}
            hasDetails={
              !!params.row.notes || !!params.row.group?.trim() || !!params.row.set?.trim()
            }
            onQuantityChange={onQuantityChange}
            onNoteClick={(id) => onNoteClick(id)}
            onDelete={onDelete}
          />
        ),
      },
    ],
    [onQuantityChange, onNoteClick, onDelete]
  );

  if (rows.length === 0) return null;

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      getRowId={(row) => row.localId}
      rowReordering
      onRowOrderChange={onReorder}
      autoHeight
      columnHeaderHeight={0}
      hideFooter
      disableColumnMenu
      disableColumnResize
      disableColumnSelector
      disableRowSelectionOnClick
      rowHeight={72}
      sx={{
        border: 'none',
        '& .MuiDataGrid-main': { borderTop: 'none' },
        '& .MuiDataGrid-topContainer': { display: 'none' },
        '& .MuiDataGrid-filler': { display: 'none' },
        '& .MuiDataGrid-row': { cursor: 'default' },
        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
        '& .MuiDataGrid-cell:first-of-type': { pl: 1.5 },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
      }}
    />
  );
};
